import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	Display,
	Flex,
	Modal,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	rem,
	Spacer,
	useBreakpointValue,
	useDisclosure,
} from '@bonusly/design-system';
import { useCurrentCompany } from '@bonusly/who-am-i';

import { useGratitudeFeedback } from '../../../gratitude-gauge/scorebox-components';
import { GiveBoxAttachment } from '../../events';
import { useBudget } from '../../hooks/use-budget';
import { clearDraft } from '../../utils/recognition-storage';
import { AmountChip } from '../amount-chip';
import { FlashPointsChip } from '../flash-points-chip';
import { GiveModalConfirmDelete } from './give-modal-confirm-delete';
import { GiveModalContent, GiveModalContentHandle } from './give-modal-content';

interface GiveModalProps {
	content?: any;
	initialAttachment?: GiveBoxAttachment;
	isOpen: boolean;
	onSuccess: VoidFunction;
	onClose: VoidFunction;
	via?: string;
	motionPreset?: 'slideInBottom' | 'slideInRight' | 'slideInLeft' | 'slideInTop' | 'scale' | 'none';
	portalProps?: any;
	transparentOverlay?: boolean;
}

export const GiveModal: FC<GiveModalProps> = ({
	content,
	initialAttachment,
	isOpen,
	onClose,
	onSuccess,
	via,
	motionPreset = 'slideInBottom',
	transparentOverlay = false,
}) => {
	const { t } = useTranslation(['common', 'recognition']);
	const budget = useBudget();
	const company = useCurrentCompany();
	const monthlyAllowanceBalanceIsZero = budget.monthlyAllowanceBalance === 0;
	const monthlyAllowanceIsZero = budget.data.allowance === 0;
	const showAmountChip = company.show_bonus_amounts !== false && !(monthlyAllowanceBalanceIsZero && monthlyAllowanceIsZero);
	const giveModalContentRef = useRef<GiveModalContentHandle>(null);
	const isOverlayClickRef = useRef(false);
	const [hasVideoAttachment, setHasVideoAttachment] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { isOpen: isConfirmModalOpen, onClose: onConfirmModalClose, onOpen: onConfirmDeleteVideoModalOpen } = useDisclosure();
	const isMobile = useBreakpointValue({ base: true, sm: false });
	const resetGratitudeFeedback = useGratitudeFeedback((state) => state.reset);

	// Save draft only when clicking outside the modal (accidental dismissal)
	const handleOverlayClick = () => {
		isOverlayClickRef.current = true;
		giveModalContentRef.current?.saveDraft();
	};

	const handleOnClose = () => {
		// If the user tries to close the modal after attaching a video,
		// we need to let them know doing so will delete the video.
		if (hasVideoAttachment) {
			onConfirmDeleteVideoModalOpen();
		} else {
			onClose();
		}
	};

	// Clear draft when modal finishes closing, but only for intentional closes (X button)
	const handleCloseComplete = () => {
		resetGratitudeFeedback();
		if (!isOverlayClickRef.current) {
			clearDraft();
		}
		isOverlayClickRef.current = false;
	};

	const handleConfirmDelete = () => {
		onConfirmModalClose();
		onClose();
		setHasVideoAttachment(false);
	};

	const mobileAttributes = {
		height: 'auto',
		minHeight: '100vh',
		m: 0,
		overflowX: 'hidden' as const,
		sx: {
			'@supports (-webkit-touch-callout: none)': {
				height: 'auto',
				minHeight: '-webkit-fill-available',
				maxHeight: 'none',
			},
		},
	};
	const desktopAttributes = {
		maxH: 'fit-content',
	};

	const mobileModalHeader = (
		<ModalHeader p={5} position="sticky" top={0} zIndex={1} bg="bg.surface" height={rem(80)} borderBottom="none" borderColor="transparent">
			<Flex alignItems="center" justifyContent="space-between" height="100%">
				<ModalCloseButton position="unset" fontSize="md" fontWeight="medium" px={10} py={5} minW={16} bg="transparent" color="secondary.fg">
					{t('cancel', { ns: 'common' })}
				</ModalCloseButton>
				<Display textAlign="center" fontSize="lg" fontWeight="semibold">
					{t('bonus_input.mobile_modal_header', { ns: 'recognition' })}
				</Display>
				<Box minW={16} textAlign="right"></Box>
			</Flex>
		</ModalHeader>
	);

	const desktopModalHeader = (
		<ModalHeader px={6} pt={5} pb={rem(4)} borderBottom="none">
			<Flex pr={0} alignItems="center">
				{t('bonus_input.modal_header_redesign', { ns: 'recognition' })}
				<Spacer />
				<FlashPointsChip />
				{showAmountChip && <AmountChip amount={budget.monthlyAllowanceBalance ?? 0} h={8} />}
				<ModalCloseButton borderRadius="full" position="static" size="md" top={0} right={0} ml={2} />
			</Flex>
		</ModalHeader>
	);

	const getHeader = () => {
		if (isMobile) {
			return mobileModalHeader;
		}
		return desktopModalHeader;
	};

	return (
		<>
			<Modal
				data-testid="give-modal"
				isOpen={isOpen}
				onClose={handleOnClose}
				onCloseComplete={handleCloseComplete}
				onOverlayClick={handleOverlayClick}
				scrollBehavior="outside"
				size={isMobile ? 'full' : 'xl'}
				motionPreset={motionPreset}
				// Should we disable the overlay click all the time?
				closeOnOverlayClick={!isSubmitting}
			>
				<ModalOverlay
					{...(transparentOverlay ? { bg: 'transparent' } : {})} // Temp Fix for Safari: Ensures modal overlay displays correctly when navigation bar is hidden
					{...(isMobile ? { minH: '200%', bg: 'bg.surface' } : {})} // Fix for IOS Keyboard resizing which hides a brief flash of the bonus feed when the keyboard opens
				/>
				<ModalContent {...(isMobile ? mobileAttributes : desktopAttributes)}>
					{getHeader()}
					<GiveModalContent
						ref={giveModalContentRef}
						content={content}
						initialAttachment={initialAttachment}
						onSuccess={onSuccess}
						setIsSubmitting={setIsSubmitting}
						onVideoAttachmentChange={setHasVideoAttachment}
						via={via}
					/>
					{isSubmitting && <Box bgColor="whiteAlpha.700" rounded="md" cursor="not-allowed" inset={0} position="absolute" />}
				</ModalContent>
			</Modal>
			<GiveModalConfirmDelete isOpen={isConfirmModalOpen} onClose={onConfirmModalClose} onConfirm={handleConfirmDelete} />
		</>
	);
};
